import * as THREE from 'three';
import { LampGeometry } from '../geometry/LampGeometry';
// --- Physical and pricing constants (tweakable) ---
const PLA_PRICE_PER_KG = 30; // USD per kg
const PLA_DENSITY_G_CM3 = 1.24; // g/cm^3
const WALL_THICKNESS_MM = 0.8; // mm
const WALL_THICKNESS_CM = WALL_THICKNESS_MM / 10; // cm
const GRAMS_PER_KG = 1000;
// --- Derived cost per cm^2 (for a 0.5mm wall) ---
// For 1 cm^2 surface, volume = 1 cm^2 * wall_thickness (cm)
// mass = volume * density (g)
// cost = (mass / 1000) * price_per_kg
const COST_PER_CM2 = (() => {
    const area_cm2 = 1;
    const volume_cm3 = area_cm2 * WALL_THICKNESS_CM; // cm^3
    const mass_g = volume_cm3 * PLA_DENSITY_G_CM3; // g
    const mass_kg = mass_g / GRAMS_PER_KG;
    return mass_kg * PLA_PRICE_PER_KG; // USD per cm^2
})();
/**
 * Estimate the surface area of a THREE.BufferGeometry (in mm^2)
 */
export function estimateSurfaceAreaFromGeometry(geometry) {
    let area = 0;
    const posAttr = geometry.getAttribute('position');
    const indexAttr = geometry.getIndex();
    if (!posAttr)
        return 0;
    const pos = posAttr.array;
    const indices = indexAttr ? indexAttr.array : undefined;
    if (indices) {
        for (let i = 0; i < indices.length; i += 3) {
            const ai = indices[i] * 3, bi = indices[i + 1] * 3, ci = indices[i + 2] * 3;
            const ax = pos[ai], ay = pos[ai + 1], az = pos[ai + 2];
            const bx = pos[bi], by = pos[bi + 1], bz = pos[bi + 2];
            const cx = pos[ci], cy = pos[ci + 1], cz = pos[ci + 2];
            // Calculate area of triangle
            const ab = new THREE.Vector3(bx - ax, by - ay, bz - az);
            const ac = new THREE.Vector3(cx - ax, cy - ay, cz - az);
            const cross = new THREE.Vector3().crossVectors(ab, ac);
            area += 0.5 * cross.length();
        }
    }
    else {
        for (let i = 0; i < pos.length; i += 9) {
            const ax = pos[i], ay = pos[i + 1], az = pos[i + 2];
            const bx = pos[i + 3], by = pos[i + 4], bz = pos[i + 5];
            const cx = pos[i + 6], cy = pos[i + 7], cz = pos[i + 8];
            const ab = new THREE.Vector3(bx - ax, by - ay, bz - az);
            const ac = new THREE.Vector3(cx - ax, cy - ay, cz - az);
            const cross = new THREE.Vector3().crossVectors(ab, ac);
            area += 0.5 * cross.length();
        }
    }
    return area; // mm^2
}
/**
 * Estimate the surface area and cost from a LampDesign
 * @returns { area: number (mm^2), cost: number ($) }
 */
export function estimateSurfaceAreaFromDesign(design) {
    const lampGeometry = new LampGeometry(design.baseShape, design.modifiers, design.noiseSeed);
    const geometry = lampGeometry.getGeometry();
    const areaMM2 = estimateSurfaceAreaFromGeometry(geometry);
    // Convert mm^2 to cm^2
    const areaCM2 = areaMM2 / 100;
    const cost = areaCM2 * COST_PER_CM2;
    return { area: areaMM2, cost };
}
